// SentEmailsView — agency-wide feed of sent (and optionally received)
// emails. Listing comes from GET /api/v1/emails with date/scope/direction
// filters. Click any row to jump to the matching client's Emails tab.
//
// Scope dropdown shows different options depending on role (the backend
// rejects mismatched requests with 403; we hide those options here so
// the user doesn't get an avoidable error).

import { useEffect, useMemo, useState } from 'react';
import { Send, Mail, Search, Calendar, ArrowLeft, ChevronRight, RefreshCw, X } from 'lucide-react';
import { listAgencyEmails } from '../api.js';
import DateField from './DateField.jsx';

// Today as 'YYYY-MM-DD' in LOCAL time. toISOString().split('T')[0] is UTC, so
// in the Florida evening (UTC-4/-5) the default date-range filters would jump
// to tomorrow and exclude emails sent "today" from the user's perspective.
const localDayKey = (date = new Date()) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

export function SentEmailsView({ currentUser, clients, setView, setOpenClientId, lang = 'en' }) {
  const es = lang === 'es';
  const role = currentUser?.role || '';
  const isPlatformOrOwner = currentUser?.isPlatformOwner || role === 'agency_owner';
  const canSeeTeam = isPlatformOrOwner || role === 'solo_agent' || role === 'agent';

  const todayKey = localDayKey();
  const sevenDaysAgo = (() => { const d = new Date(); d.setDate(d.getDate() - 7); return localDayKey(d); })();

  const [direction, setDirection] = useState('OUTBOUND');
  const [scope, setScope] = useState('mine');
  const [fromDate, setFromDate] = useState(sevenDaysAgo);
  const [toDate, setToDate] = useState(todayKey);
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    listAgencyEmails({ direction, scope, fromDate, toDate, page, limit: 50 })
      .then((res) => {
        if (cancelled) return;
        setRows(res.data);
        setPagination(res.pagination);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Failed to load');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [direction, scope, fromDate, toDate, page]);

  // Client-side search filter over the page we already have. The backend
  // doesn't support full-text yet — search drills into subject + addresses
  // + the local client name lookup so the user can find a thread quickly
  // without firing a request per keystroke.
  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) => {
      const client = r.client || clients.find((c) => c.id === r.clientId);
      const clientName = client ? `${client.firstName} ${client.lastName}`.toLowerCase() : '';
      return (
        (r.subject || '').toLowerCase().includes(q) ||
        (r.toAddress || '').toLowerCase().includes(q) ||
        (r.fromAddress || '').toLowerCase().includes(q) ||
        clientName.includes(q)
      );
    });
  }, [rows, search, clients]);

  const onRowClick = (row) => {
    if (!row.clientId) return; // orphan inbound — nothing to navigate to
    if (setOpenClientId) setOpenClientId(row.clientId);
    if (setView) setView('clients');
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
    const rowDay = new Date(d); rowDay.setHours(0, 0, 0, 0);
    const timeStr = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    if (rowDay.getTime() === today.getTime()) return `${es ? 'Hoy' : 'Today'}, ${timeStr}`;
    if (rowDay.getTime() === yesterday.getTime()) return `${es ? 'Ayer' : 'Yesterday'}, ${timeStr}`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + `, ${timeStr}`;
  };

  const truncate = (s, n) => {
    if (!s) return '';
    return s.length > n ? s.slice(0, n - 1) + '…' : s;
  };

  return (
    <div style={{ padding: '0' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem' }}>
          <span style={{ fontSize: '1.4rem' }}>📤</span>
          <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)' }}>
            {es ? 'Emails Enviados' : 'Sent Emails'}
          </h2>
          {pagination && (
            <span style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', fontFamily: 'var(--font-mono)' }}>
              · {pagination.total} {direction === 'INBOUND' ? (es ? 'recibidos' : 'received') : (es ? 'enviados' : 'sent')}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => { setPage(1); setFromDate(sevenDaysAgo); setToDate(todayKey); }}
          className="dkt-btn-g"
          title={es ? 'Refrescar' : 'Refresh'}
        >
          <RefreshCw size={12} />
        </button>
      </div>

      {/* Filters bar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem', marginBottom: '1rem', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '.3rem' }}>
          <button
            type="button"
            onClick={() => { setDirection('OUTBOUND'); setPage(1); }}
            className={`dkt-tab${direction === 'OUTBOUND' ? ' on' : ''}`}
          >
            <Send size={11} /> {es ? 'Enviados' : 'Sent'}
          </button>
          <button
            type="button"
            onClick={() => { setDirection('INBOUND'); setPage(1); }}
            className={`dkt-tab${direction === 'INBOUND' ? ' on' : ''}`}
          >
            <Mail size={11} /> {es ? 'Recibidos' : 'Received'}
          </button>
        </div>

        {canSeeTeam && (
          <select
            value={scope}
            onChange={(e) => { setScope(e.target.value); setPage(1); }}
            className="dkt-inp"
            style={{ fontSize: '.73rem', padding: '.4rem .75rem', width: 'auto', cursor: 'pointer' }}
          >
            <option value="mine">{es ? '👤 Solo míos' : '👤 Only mine'}</option>
            <option value="team">{es ? '👥 Mi equipo (yo + asientos)' : '👥 My team (me + seats)'}</option>
            {isPlatformOrOwner && (
              <option value="agency">{es ? '🏢 Toda la agencia' : '🏢 Whole agency'}</option>
            )}
          </select>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Calendar size={11} style={{ color: 'var(--c-text3,#45688a)' }} />
          <DateField
            value={fromDate}
            max={toDate}
            onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
            style={{ width: 132, padding: '.35rem .5rem', borderRadius: 6, border: '1px solid var(--c-border,#1a3550)', background: 'var(--c-surface2,#112538)', color: 'var(--c-text2,#7aa3c0)', fontSize: '.7rem', fontFamily: 'var(--font-mono)', cursor: 'pointer' }}
          />
          <span style={{ color: 'var(--c-text3,#45688a)', fontSize: '.7rem' }}>→</span>
          <DateField
            value={toDate}
            min={fromDate}
            max={todayKey}
            onChange={(e) => { setToDate(e.target.value); setPage(1); }}
            style={{ width: 132, padding: '.35rem .5rem', borderRadius: 6, border: '1px solid var(--c-border,#1a3550)', background: 'var(--c-surface2,#112538)', color: 'var(--c-text2,#7aa3c0)', fontSize: '.7rem', fontFamily: 'var(--font-mono)', cursor: 'pointer' }}
          />
        </div>

        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={12} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-text3,#45688a)', pointerEvents: 'none' }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={es ? 'Buscar asunto, cliente, email…' : 'Search subject, client, email…'}
            className="dkt-search"
            style={{ paddingLeft: 30 }}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text3,#45688a)' }}
            >
              <X size={12} />
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      {loading && (
        <div className="dkt-card" style={{ textAlign: 'center', padding: '2rem', color: 'var(--c-text3,#45688a)', fontSize: '.85rem' }}>
          {es ? 'Cargando…' : 'Loading…'}
        </div>
      )}
      {!loading && error && (
        <div className="dkt-card" style={{ textAlign: 'center', padding: '2rem', color: 'var(--c-red,#ef4444)', fontSize: '.85rem' }}>
          {error}
        </div>
      )}
      {!loading && !error && filteredRows.length === 0 && (
        <div className="dkt-card" style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--c-text3,#45688a)', fontSize: '.85rem' }}>
          {direction === 'INBOUND'
            ? (es ? 'No hay emails recibidos en este rango.' : 'No received emails in this range.')
            : (es ? 'No hay emails enviados en este rango. Manda un email a un cliente desde su perfil o desde Campañas.' : 'No sent emails in this range. Send one from a client profile or from Campaigns.')}
        </div>
      )}
      {!loading && !error && filteredRows.length > 0 && (
        <div className="space-y-1">
          {filteredRows.map((row) => {
            const client = row.client || clients.find((c) => c.id === row.clientId);
            const clientName = client ? `${client.firstName} ${client.lastName}` : (es ? 'Sin cliente' : 'No client');
            return (
              <div
                key={row.id}
                onClick={() => onRowClick(row)}
                className="dkt-card"
                style={{ padding: '.75rem 1rem', cursor: row.clientId ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: '.75rem' }}
              >
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: direction === 'INBOUND' ? 'rgba(0,212,255,.15)' : 'rgba(16,185,129,.15)', color: direction === 'INBOUND' ? 'var(--c-cyan,#00d4ff)' : '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {direction === 'INBOUND' ? <Mail size={14} /> : <Send size={14} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: 2 }}>
                    <strong style={{ fontSize: '.85rem', color: 'var(--c-white,#eef6ff)' }}>{clientName}</strong>
                    <span style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)' }}>
                      {direction === 'INBOUND' ? (es ? 'de' : 'from') : (es ? 'a' : 'to')} {row.toAddress || row.fromAddress}
                    </span>
                  </div>
                  <div style={{ fontSize: '.78rem', color: 'var(--c-text2,#a0b8d0)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {row.subject || (es ? '(sin asunto)' : '(no subject)')}
                    {row.bodyText && (
                      <span style={{ color: 'var(--c-text3,#45688a)' }}> — {truncate(row.bodyText.replace(/\s+/g, ' '), 90)}</span>
                    )}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: '.65rem', color: 'var(--c-text3,#45688a)', fontFamily: 'var(--font-mono)', marginBottom: 2 }}>
                    {fmtDate(row.sentAt || row.receivedAt || row.createdAt)}
                  </div>
                  {row.agentUser && scope !== 'mine' && (
                    <div style={{ fontSize: '.65rem', color: 'var(--c-cyan,#00d4ff)' }}>
                      {row.agentUser.displayName}
                    </div>
                  )}
                </div>
                {row.clientId && <ChevronRight size={14} style={{ color: 'var(--c-text3,#45688a)', flexShrink: 0 }} />}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '.5rem', marginTop: '1rem' }}>
          <button
            type="button"
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="dkt-btn-g"
            style={{ opacity: page <= 1 ? 0.4 : 1, cursor: page <= 1 ? 'not-allowed' : 'pointer' }}
          >
            <ArrowLeft size={12} /> {es ? 'Anterior' : 'Previous'}
          </button>
          <span style={{ fontSize: '.72rem', color: 'var(--c-text2,#7aa3c0)', fontFamily: 'var(--font-mono)' }}>
            {page} / {pagination.totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage(Math.min(pagination.totalPages, page + 1))}
            disabled={page >= pagination.totalPages}
            className="dkt-btn-g"
            style={{ opacity: page >= pagination.totalPages ? 0.4 : 1, cursor: page >= pagination.totalPages ? 'not-allowed' : 'pointer' }}
          >
            {es ? 'Siguiente' : 'Next'} <ChevronRight size={12} />
          </button>
        </div>
      )}
    </div>
  );
}
